import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';
import { COBERTURA_EXPLICACIONES, COBERTURA_ROW_STYLES } from '../../../utils/finanzasUi';

function FilaDetalle({ titulo, count, pct, tipo }) {
  return (
    <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className={`font-semibold ${COBERTURA_ROW_STYLES[tipo]}`}>{titulo}</p>
        <span className={`text-lg font-bold ${COBERTURA_ROW_STYLES[tipo]}`}>{pct ?? 0} %</span>
      </div>
      <p className="mt-1 text-sm text-slate-600">
        {typeof count === 'number' ? `${count} venta${count === 1 ? '' : 's'}` : '—'}
      </p>
      <p className="mt-2 text-xs leading-relaxed text-slate-500">{COBERTURA_EXPLICACIONES[tipo]}</p>
    </div>
  );
}

export default function CoberturaDetalleModal({ isOpen, onClose, cobertura }) {
  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleClose();
      }
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, handleClose]);

  if (!isOpen || !cobertura) return null;

  const total = cobertura.ventas_total
    ?? ((cobertura.ventas_real || 0) + (cobertura.ventas_estimada || 0) + (cobertura.ventas_no_calculable || 0));

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Cerrar detalle de cobertura"
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-[1px] cursor-default"
        onClick={handleClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="cobertura-detalle-title"
        className="relative z-10 w-full max-w-lg max-h-[min(90vh,640px)] overflow-y-auto rounded-2xl border border-slate-200/80 bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-slate-100 bg-white px-5 py-4">
          <h3 id="cobertura-detalle-title" className="text-lg font-semibold text-slate-900">
            Detalle de cobertura financiera
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="shrink-0 rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            aria-label="Cerrar"
          >
            <FaTimes className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3 px-5 py-4 text-slate-800">
          <p className="text-sm text-slate-600">
            De <strong>{total}</strong> ventas comerciales del período, así se distribuye la cobertura:
          </p>
          <FilaDetalle
            titulo="Ventas reales"
            count={cobertura.ventas_real}
            pct={cobertura.pct_real}
            tipo="real"
          />
          <FilaDetalle
            titulo="Ventas estimadas"
            count={cobertura.ventas_estimada}
            pct={cobertura.pct_estimada}
            tipo="estimada"
          />
          <FilaDetalle
            titulo="Ventas no calculables"
            count={cobertura.ventas_no_calculable}
            pct={cobertura.pct_no_calculable}
            tipo="no_calculable"
          />
        </div>
        <div className="border-t border-slate-100 px-5 py-3 bg-slate-50/80">
          <button
            type="button"
            onClick={handleClose}
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
